import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useAuthStore } from './stores/authStore';
import LoginPage from './pages/LoginPage';
import PortalPage from './pages/PortalPage';
import MainLayout from './layouts/MainLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import LazyPage from './components/LazyPage';

// 懒加载其他页面
const BranchListPage = React.lazy(() => import('./pages/branches/BranchListPage'));
const StudentListPage = React.lazy(() => import('./pages/students/StudentListPage'));
const GradeBatchListPage = React.lazy(() => import('./pages/grades/GradeBatchListPage'));
const UserListPage = React.lazy(() => import('./pages/admin/UserListPage'));
const PermissionManagePage = React.lazy(() => import('./pages/admin/PermissionManagePage'));
const CourseListPage = React.lazy(() => import('./pages/courses/CourseListPage'));
const CourseDetailPage = React.lazy(() => import('./pages/courses/CourseDetailPage'));
const ActivityListPage = React.lazy(() => import('./pages/activities/ActivityListPage'));
const CompetitionListPage = React.lazy(() => import('./pages/competitions/CompetitionListPage'));
const MyCoursesPage = React.lazy(() => import('./pages/students/MyCoursesPage'));
const StudentCourseDetailPage = React.lazy(() => import('./pages/students/StudentCourseDetailPage'));
const StudentResearchPage = React.lazy(() => import('./pages/courses/StudentResearchPage'));
const MyCompetitionsPage = React.lazy(() => import('./pages/students/MyCompetitionsPage'));
const TeacherListPage = React.lazy(() => import('./pages/teachers/TeacherListPage'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));

// 路由守卫
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return (
    <Routes>
      <Route path="/" element={<PortalPage />} />
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="profile" element={
          <LazyPage><ProfilePage /></LazyPage>
        } />
        <Route path="branches" element={
          <LazyPage><BranchListPage /></LazyPage>
        } />
        <Route path="teachers" element={
          <LazyPage><TeacherListPage /></LazyPage>
        } />
        <Route path="students" element={
          <LazyPage><StudentListPage /></LazyPage>
        } />
        <Route path="grades" element={
          <LazyPage><GradeBatchListPage /></LazyPage>
        } />
        <Route path="admin/users" element={
          <LazyPage><UserListPage /></LazyPage>
        } />
        <Route path="admin/permissions" element={
          <LazyPage><PermissionManagePage /></LazyPage>
        } />
        <Route path="courses" element={
          <LazyPage><CourseListPage /></LazyPage>
        } />
        <Route path="courses/:id" element={
          <LazyPage><CourseDetailPage /></LazyPage>
        } />
        <Route path="activities" element={
          <LazyPage><ActivityListPage /></LazyPage>
        } />
        <Route path="competitions" element={
          <LazyPage><CompetitionListPage /></LazyPage>
        } />
        <Route path="my-courses" element={
          <LazyPage><MyCoursesPage /></LazyPage>
        } />
        <Route path="my-courses/:id" element={
          <LazyPage><StudentCourseDetailPage /></LazyPage>
        } />
        <Route path="my-research" element={
          <LazyPage><StudentResearchPage /></LazyPage>
        } />
        <Route path="my-competitions" element={
          <LazyPage><MyCompetitionsPage /></LazyPage>
        } />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

const AppRoot: React.FC = () => (
  <ConfigProvider
    locale={zhCN}
    theme={{
      token: {
        colorPrimary: '#16a34a',
        borderRadius: 6,
        fontFamily: '-apple-system, "PingFang SC", "Microsoft YaHei", sans-serif',
      },
    }}
  >
    <App>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </App>
  </ConfigProvider>
);

export default AppRoot;
